import { NgClass } from '@angular/common';
import {
    Component,
    DestroyRef,
    inject,
    type OnInit,
    signal,
    type WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from '@openng/optimus-ui/button';
import { DividerModule } from '@openng/optimus-ui/divider';
import { IconFieldModule } from '@openng/optimus-ui/iconfield';
import { InputIconModule } from '@openng/optimus-ui/inputicon';
import { InputOtpModule } from '@openng/optimus-ui/inputotp';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { MessageModule } from '@openng/optimus-ui/message';
import { TagModule } from '@openng/optimus-ui/tag';
import { TimelineModule } from '@openng/optimus-ui/timeline';
import { trim } from 'lodash-es';
import { injectQueryParams } from 'ngxtension/inject-query-params';
import { from, switchMap } from 'rxjs';

import { AUTH_TYPE, AuthenticationService } from '@app/auth/services/authentication.service';
import {
    API_PREFIX,
    OAUTH_SSO_PATH,
    OK_RESPONSE,
    SAML_SSO_PATH,
} from '@app/shared/constants/shared.constant';
import { IncludesPipe } from '@app/shared/pipes/includes.pipe';
import { AppConfigService } from '@app/shared/services/app-config.service';
import { StorageService } from '@app/shared/services/storage.service';
import { ThemeService } from '@app/shared/services/theme.service';
import { ToastNotificationService } from '@app/shared/services/toast-notification.service';
import { WindowService } from '@app/shared/services/window.service';
import { Helpers } from '@app/shared/utils/helpers';

const LOGIN_STATE_KEY = 'login_preference';

@Component({
    selector: 'cor-login',
    templateUrl: './login.component.html',
    styleUrl: './login.component.scss',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        InputOtpModule,
        InputTextModule,
        IconFieldModule,
        InputIconModule,
        ButtonModule,
        NgClass,
        IncludesPipe,
        DividerModule,
        TagModule,
        TimelineModule,
        MessageModule,
    ],
})
export class LoginComponent implements OnInit {
    private readonly returnUrl = injectQueryParams('returnUrl');

    private readonly authService = inject(AuthenticationService);
    private readonly storageService = inject(StorageService);
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);
    private readonly toast = inject(ToastNotificationService);
    private readonly windowService = inject(WindowService);
    protected readonly appConfig = inject(AppConfigService).appConfig;
    protected readonly themeService = inject(ThemeService);

    protected readonly AUTH_TYPE = AUTH_TYPE;

    protected loginForm = new FormGroup({
        // Password Fields
        username: new FormControl<string>(null, [Validators.required]),
        password: new FormControl('', Validators.required),
        // OTP Fields
        otp: new FormControl<string>(null, Validators.required),
        // Type: OTP or Password
        type: new FormControl<AUTH_TYPE>(AUTH_TYPE.PASSWORD),
    });

    protected readonly showPassword = signal<boolean>(false);
    protected readonly loading = signal<AUTH_TYPE>(null);
    protected readonly showLogin = signal<boolean>(false);
    protected readonly isOTPSent = signal(false);
    protected readonly requestingOTP = signal(false);
    protected readonly loginError = signal(null);

    protected readonly info = [
        {
            title: 'Connect',
            description:
                'Seamlessly connect to your data lake and unlock actionable insights with our comprehensive Data Vault',
            icon: 'pi pi-database tw:text-blue-600',
            iconBg: 'tw:bg-blue-500/10',
        },
        {
            title: 'Build',
            description:
                'Accelerate AI development with GenAI Studio and build pipelines for your use cases',
            icon: 'pi pi-gauge tw:text-purple-600',
            iconBg: 'tw:bg-purple-500/10',
        },
        {
            title: 'Evaluate',
            description:
                'Enhance pipeline accuracy with HIT, our innovative solution for Human Integrated Testing and scoring',
            icon: 'pi pi-chart-line tw:text-green-600',
            iconBg: 'tw:bg-green-500/10',
        },
    ];

    ngOnInit() {
        if (this.appConfig().allowedLogins.includes(AUTH_TYPE.OTP)) {
            this.loginForm.reset({ type: AUTH_TYPE.OTP }, { emitEvent: false });
            this.onLoginTypeChange(AUTH_TYPE.OTP); // Update validators
        }
        this.showLogin.set(true);

        this.fillCachedUserInfo(this.loginForm.controls.type.value);
    }

    private fillCachedUserInfo(loginType: string) {
        const userInfo = this.storageService.getLocalStorage(LOGIN_STATE_KEY);
        if (!userInfo) return;

        const { value, type } = JSON.parse(atob(trim(userInfo, `"`)));
        if (type === loginType) {
            this.loginForm.controls.username.setValue(value);
        }
    }

    /**
     * Send OTP to the user's email for the OTP login flow.
     *
     * This method will be called when the user clicks the "Send OTP" button.
     * It will send a request to the backend to send an OTP to the user's email.
     * If the request is successful, it will show a success toast message.
     */
    protected requestEmailOTP() {
        this.loginError.set(null);
        this.loginForm.controls.otp.reset();
        if (this.loginForm.controls.username.invalid) {
            this.loginForm.controls.username.markAsTouched();

            return;
        }
        this.requestingOTP.set(true);
        this.authService
            .requestOTP(this.loginForm.controls.username.value.trim())
            .subscribe({
                next: response => {
                    if (response.status === OK_RESPONSE) {
                        this.isOTPSent.set(true);
                        this.toast.showSuccess('Verification Code sent successfully.');
                    }
                },
                error: error => {
                    this.loginError.set(
                        Helpers.getErrorMessageString(error) ||
                            error?.error?.message ||
                            'Unable to complete your request. Please try again.',
                    );
                },
            })
            .add(() => {
                this.requestingOTP.set(false);
            });
    }

    protected toggleSignal(sig: WritableSignal<boolean>) {
        sig.update(x => !x);
    }

    protected onLoginTypeChange(value: AUTH_TYPE) {
        this.loginError.set(null);
        this.isOTPSent.set(false);
        this.loginForm.reset(
            { username: this.loginForm.controls.username.value, type: value },
            { emitEvent: false },
        );

        if (!this.loginForm.controls.username.value) {
            this.fillCachedUserInfo(value);
        }
    }

    protected signin() {
        this.loginError.set(null);
        const { username, password, otp, type } = this.loginForm.value;

        this.loginForm.markAsTouched();

        if (type === AUTH_TYPE.OTP && !otp) {
            this.loginError.set('Please enter Verification Code');

            return;
        }
        if (type === AUTH_TYPE.PASSWORD && (!password || !username)) {
            this.loginError.set('Please enter Username and Password');

            return;
        }

        let payload = { email: '', password: '', type };

        if (type === AUTH_TYPE.OTP && this.loginForm.controls.otp.valid) {
            payload = { email: username.trim(), password: otp.trim(), type };
        }
        if (type === AUTH_TYPE.PASSWORD) {
            payload = { email: username.trim(), password, type };
        }

        this.loading.set(type);
        this.authService
            .attemptLogin(payload)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap(() => {
                    const returnUrl = this.returnUrl() || '/home';
                    this.storageService.setLocalStorage(
                        LOGIN_STATE_KEY,
                        btoa(JSON.stringify({ value: username, type })),
                    );
                    return from(this.router.navigateByUrl(returnUrl));
                }),
            )
            .subscribe({
                error: error => {
                    /**
                     * If credentials are wrong then the sign-in loader will get cancelled or else the
                     * loader will be present until user is navigated inside the platform
                     */
                    this.loginError.set(
                        Helpers.getErrorMessageString(error) ||
                            error?.error?.message ||
                            'Unable to sign in. Please try again.',
                    );
                },
            })
            .add(() => this.loading.set(null));
    }

    protected samlSSOLogin() {
        this.windowService.replaceUrl(
            `${this.appConfig().backendUrl}${API_PREFIX}${SAML_SSO_PATH}`,
        );
    }

    protected oauthLogin(oauthProvider: AUTH_TYPE.GOOGLE | AUTH_TYPE.MICROSOFT) {
        this.loading.set(oauthProvider);
        const queryParams = new URLSearchParams({ provider: oauthProvider }).toString();
        this.windowService.replaceUrl(
            `${this.appConfig().backendUrl}${API_PREFIX}${OAUTH_SSO_PATH}?${queryParams}`,
        );
    }
}
